import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Package, Truck, CheckCircle2, Clock, Factory, Home, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FULFILL = {
  aguardando_pagamento: { label: 'Aguardando pagamento', icon: Clock, color: 'text-amber-600', step: 0 },
  em_producao: { label: 'Em produção', icon: Factory, color: 'text-blue-600', step: 1 },
  em_preparacao: { label: 'Em preparação para envio', icon: Package, color: 'text-indigo-600', step: 2 },
  enviado: { label: 'Enviado', icon: Truck, color: 'text-purple-600', step: 3 },
  entregue: { label: 'Entregue', icon: CheckCircle2, color: 'text-emerald-600', step: 4 },
  cancelado: { label: 'Cancelado', icon: Clock, color: 'text-red-600', step: -1 },
};

const PAYMENT_LABEL = {
  approved: { label: 'Aprovado', class: 'bg-emerald-100 text-emerald-800' },
  pending: { label: 'Pendente', class: 'bg-amber-100 text-amber-800' },
  in_process: { label: 'Em análise', class: 'bg-amber-100 text-amber-800' },
  rejected: { label: 'Recusado', class: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelado', class: 'bg-neutral-200 text-neutral-700' },
};

const Stepper = ({ current }) => {
  const steps = [
    { key: 'em_producao', label: 'Em produção', Icon: Factory },
    { key: 'em_preparacao', label: 'Preparação', Icon: Package },
    { key: 'enviado', label: 'Enviado', Icon: Truck },
    { key: 'entregue', label: 'Entregue', Icon: Home },
  ];
  const idx = FULFILL[current]?.step ?? 0;
  return (
    <div className="flex items-center justify-between gap-2 my-8">
      {steps.map((s, i) => {
        const active = i + 1 <= idx;
        const isCurrent = i + 1 === idx;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                active ? 'bg-neutral-900 border-neutral-900 text-white' : 'bg-white border-neutral-300 text-neutral-400'
              } ${isCurrent ? 'ring-4 ring-neutral-900/10' : ''}`}>
                <s.Icon className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <span className={`text-[10px] uppercase tracking-widest mt-2 text-center ${active ? 'text-neutral-900' : 'text-neutral-400'}`}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-px flex-1 -mt-6 ${idx > i + 1 ? 'bg-neutral-900' : 'bg-neutral-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const MyOrders = () => {
  usePageTitle('Meus Pedidos');
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login?next=/meus-pedidos');
      return;
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/payments/orders/mine`);
      setOrders(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return null;

  return (
    <div data-testid="my-orders-page" className="min-h-screen bg-white pt-32 pb-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto">
        <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-2">
          Casa in Gold · {user?.name || user?.email}
        </p>
        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-neutral-900 mb-12">
          Meus Pedidos
        </h1>

        {loading ? (
          <p className="text-sm text-neutral-500">Carregando seus pedidos…</p>
        ) : orders.length === 0 ? (
          <div className="border border-dashed border-neutral-300 py-16 text-center">
            <p className="text-neutral-600 mb-6">Você ainda não fez nenhum pedido.</p>
            <Link
              to="/catalogo"
              className="inline-flex items-center gap-2 bg-neutral-900 text-white px-8 py-3 uppercase tracking-widest text-xs hover:bg-black"
            >
              Explorar Catálogo <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {orders.map((o) => {
              const status = FULFILL[o.fulfillment_status || (o.payment_status === 'approved' ? 'em_producao' : 'aguardando_pagamento')];
              const pay = PAYMENT_LABEL[o.payment_status] || { label: o.payment_status || '—', class: 'bg-neutral-100 text-neutral-700' };
              const StatusIcon = status?.icon || Clock;
              return (
                <div
                  key={o.order_id}
                  data-testid={`order-card-${o.order_id}`}
                  className="border border-neutral-200 p-8"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div>
                      <p className="font-mono text-xs text-neutral-500 mb-1">Pedido #{o.order_id?.slice(0, 8)}</p>
                      <p className="font-heading text-2xl text-neutral-900">
                        R$ {Number(o.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-neutral-500 mt-1">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
                      </p>
                    </div>
                    <div className="flex flex-col md:items-end gap-2">
                      <span className={`text-xs px-3 py-1 ${pay.class}`}>Pagamento: {pay.label}</span>
                      <span className={`inline-flex items-center gap-2 text-sm font-medium ${status?.color}`}>
                        <StatusIcon className="w-4 h-4" strokeWidth={1.5} />
                        {status?.label || o.fulfillment_status || '—'}
                      </span>
                    </div>
                  </div>

                  {/* Stepper visual */}
                  {o.payment_status === 'approved' && o.fulfillment_status !== 'cancelado' && (
                    <Stepper current={o.fulfillment_status || 'em_producao'} />
                  )}

                  {/* Itens */}
                  {(o.items || []).length > 0 && (
                    <div className="pt-6 border-t border-neutral-200">
                      <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Itens do pedido</p>
                      <ul className="space-y-2">
                        {(o.items || []).map((it, i) => (
                          <li key={`${it.title || 'item'}-${i}`} className="flex justify-between text-sm">
                            <span className="text-neutral-700">{it.title} × {it.quantity}</span>
                            <span className="text-neutral-900">
                              R$ {(Number(it.unit_price || 0) * Number(it.quantity || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {o.tracking_code && (
                    <div className="mt-4 pt-4 border-t border-neutral-200 flex items-center gap-2">
                      <Truck className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
                      <span className="text-xs text-neutral-500">Código de rastreio:</span>
                      <span className="font-mono text-sm text-neutral-900">{o.tracking_code}</span>
                    </div>
                  )}

                  {o.fulfillment_note && (
                    <p className="mt-4 text-xs text-neutral-600 italic">{o.fulfillment_note}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
